import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { attemptRiderLogin } from '@/services/riderAuthService';
import { supabase } from '@/integrations/supabase/client';

// Helper to log staff/rider auth events
const logStaffAuthEvent = async (
  restaurantId: string,
  staffId: string,
  staffName: string,
  action: 'login' | 'logout',
  userType: 'staff' | 'rider' = 'staff'
) => {
  try {
    await supabase.from('audit_logs').insert({
      restaurant_id: restaurantId,
      staff_id: staffId,
      user_type: userType,
      user_name: staffName,
      action: action,
      entity_type: 'authentication',
      entity_id: staffId,
      entity_name: staffName,
      changes: {},
      metadata: { event: action, userType }
    });
  } catch (error) {
    console.error('Failed to log staff auth event:', error);
  }
};

export default function Auth() {
  const { signIn, signUp, user, setStaffSession, setRiderSession, authenticating } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  
  // Signup form state
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [fullName, setFullName] = useState('');
  
  // Staff login form state
  const [staffUsername, setStaffUsername] = useState('');
  const [staffPassword, setStaffPassword] = useState('');

  // Rider login form state (same as staff - username/password)
  const [riderUsername, setRiderUsername] = useState('');
  const [riderPassword, setRiderPassword] = useState('');

  // Redirect if user is already logged in
  useEffect(() => {
    if (user && !authenticating) {
      navigate('/dashboard');
    }
  }, [user, authenticating, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await signIn(loginEmail, loginPassword);
      
      if (result.error) {
        setError(result.error.message);
      }
    } catch (err) {
      console.error('Unexpected error during login:', err);
      setError('An unexpected error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const { error } = await signUp(signupEmail, signupPassword, fullName);
    
    if (error) {
      setError(error.message);
    } else {
      setError('Please check your email to confirm your account.');
    }
    
    setLoading(false);
  };

  const handleStaffLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Use secure RPC for login
      const { data: staffUser, error: loginError } = await supabase
        .rpc('login_staff', { 
          p_username: staffUsername, 
          p_password: staffPassword 
        });

      if (loginError) {
        console.error('Login error:', loginError);
        setError('Invalid username or password');
      } else if (staffUser) {
          // Store staff session in localStorage
           const staffSession = {
             id: staffUser.id,
             username: staffUser.username,
             restaurant_id: staffUser.restaurant_id,
             access_permissions: staffUser.access_permissions,
             allowed_branches: staffUser.allowed_branches || [],
             isStaff: true
           };
          
          localStorage.setItem('staff_session', JSON.stringify(staffSession));
          localStorage.setItem(`staff_permissions_${staffUser.id}`, JSON.stringify(staffUser.access_permissions));
          
          // Log the staff login event
          await logStaffAuthEvent(
            staffUser.restaurant_id,
            staffUser.id,
            staffUser.username,
            'login',
            'staff'
          );
          
          // Update AuthContext with the new staff session
          setStaffSession(staffSession);
          
          // Redirect to dashboard
          navigate('/dashboard');
      } else {
        setError('Invalid username or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const handleRiderLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await attemptRiderLogin(riderUsername, riderPassword);
      
      if (result.success && result.rider) {
        // Log the rider login event
        await logStaffAuthEvent(
          result.rider.restaurant_id,
          result.rider.id,
          result.rider.name,
          'login',
          'rider'
        );
        
        // Set rider session in AuthContext
        setRiderSession(result.rider);
        
        // Redirect to rider dashboard
        navigate('/rider-dashboard');
      } else {
        setError(result.error || 'Invalid username or password');
      }
    } catch (error) {
      console.error('Rider login error:', error);
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <Link to="/" className="mb-8">
        <Button variant="ghost" className="flex items-center gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Home
        </Button>
      </Link>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Restaurant Manager</CardTitle>
          <CardDescription>
            Manage your restaurant menus and QR codes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="login">Owner</TabsTrigger>
              <TabsTrigger value="staff">Staff</TabsTrigger>
              <TabsTrigger value="rider">Rider</TabsTrigger>
              <TabsTrigger value="signup">Sign Up</TabsTrigger>
            </TabsList>
            
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="Enter your email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="Enter your password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="staff">
              <form onSubmit={handleStaffLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="staff-username">Username</Label>
                  <Input
                    id="staff-username"
                    type="text"
                    placeholder="Enter your username"
                    value={staffUsername}
                    onChange={(e) => setStaffUsername(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="staff-password">Password</Label>
                  <Input
                    id="staff-password"
                    type="password"
                    placeholder="Enter your password"
                    value={staffPassword}
                    onChange={(e) => setStaffPassword(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Staff Sign In'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="rider">
              <form onSubmit={handleRiderLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="rider-username">Username</Label>
                  <Input
                    id="rider-username"
                    type="text"
                    placeholder="Enter your username"
                    value={riderUsername}
                    onChange={(e) => setRiderUsername(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rider-password">Password</Label>
                  <Input
                    id="rider-password"
                    type="password"
                    placeholder="Enter your password"
                    value={riderPassword}
                    onChange={(e) => setRiderPassword(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Rider Sign In'}
                </Button>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full-name">Full Name</Label>
                  <Input
                    id="full-name"
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    type="email"
                    placeholder="Enter your email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    placeholder="Choose a password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                  />
                </div>
                {error && (
                  <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
